import type { RouteRecordRaw } from 'vue-router'

import { keys } from '@/app/i18n'
import { ROUTE_NOT_AUTHORIZED } from '@/app/router/names'
import { notFoundLocation } from '@/app/router/not-found'
import { useAuthStore } from '@/app/stores/auth'
import { EMPLOYER_SOFTWARES } from '@/app/stores/session'

import { setTabCrumb, settingsCrumb } from './breadcrumb'

/**
 * The settings module's routes (WP-05a, wp/settings §3). Paths and names from the route manifest (D-15).
 *
 * - `settings`: one route for every tab, for the five employer softwares: a software the user lacks is the
 *   not-authorized page (the shell's guard, D-21), `ca` / `em` (a Freelancer's) and unknown codes the 404 page (legacy
 *   404'd `ca` only — NEW-28). The param has no pattern on purpose: a pattern would outrank the generated redirects of
 *   the misspelt legacy slugs. No ACL resource: each tab is gated by the registry (FX-070). The breadcrumb "Settings" opens Configuration and the tab's label follows it (NEW-2; legacy
 *   linked `/<sw>/settings`, which matched no route).
 * - the manifest's corrected pipeline slugs are declared as redirects onto `settings`, so no "Not migrated yet"
 *   placeholder of those paths shadows the tab route (the misspelt legacy slugs redirect to them, D-15).
 * - `connect-stripe`: the Stripe Connect OAuth return (no software segment). The API's call is Client only, so the
 *   page is too (wp/settings §1.4 decision; legacy let anyone in and showed the 403 as a toast).
 */
const PIPELINE_SLUGS = ['recruiting-pipeline', 'onboarding-pipeline', 'offboarding-pipeline'] as const

const routes: RouteRecordRaw[] = [
  {
    path: '/:software/settings/:tab',
    name: 'settings',
    component: () => import('./views/SettingsView.vue'),
    beforeEnter: (to) => {
      if (!(EMPLOYER_SOFTWARES as readonly string[]).includes(String(to.params.software)))
        return notFoundLocation(to)
      setTabCrumb(to)
      return true
    },
    meta: {
      software: 'common',
      pageTitle: keys.t('settings.shell.title.settings'),
      breadcrumb: [settingsCrumb],
    },
  },
  ...PIPELINE_SLUGS.map((slug): RouteRecordRaw => ({
    path: `/:software/settings/${slug}`,
    redirect: (to) => ({
      name: 'settings',
      params: { software: to.params.software, tab: slug },
      query: to.query,
      hash: to.hash,
    }),
  })),
  {
    path: '/settings/connect/:provider',
    name: 'connect-stripe',
    component: () => import('./views/ConnectStripeView.vue'),
    beforeEnter: () => (useAuthStore().session?.userType === 'Client' ? true : { name: ROUTE_NOT_AUTHORIZED }),
    meta: {
      pageTitle: keys.t('settings.connect.title.page'),
      breadcrumb: [
        {
          text: keys.t('settings.shell.title.settings'),
          to: { name: 'settings', params: { software: 'ac', tab: 'payment-providers' } },
        },
        { text: keys.t('settings.connect.title.page'), active: true },
      ],
    },
  },
]

export default routes
